import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  setDoc,
  updateDoc,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { firestore } from '../firebase';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import { getDisplayName, getFreshlingDisplayName, getFirstName } from '../utils/flyerDisplay';
import { copyText, buildTsvTable } from '../utils/clipboardUtils';
import { openPrintWindowOptions, openPrintWindow, getOperationalTablePrintCss } from '../utils/printUtils';
import AdminCopyMenu from './admin/AdminCopyMenu';
import AdminColumnExportModal from './admin/AdminColumnExportModal';
import {
  normalizeSize,
  getRequestedSize,
  isWithdrawnOrDuplicate,
  isWaitingMentor,
  dedupeFlyersByEmail,
  getRegistrationDateMs,
  getCutoffMs,
  sumSavedInventoryOrdered,
  TSHIRT_NO_ALLOCATION_VALUE,
} from '../utils/tshirtRosterEligibility';
import { collectInvalidNamedControlNames } from '../utils/collectInvalidNamedControlNames';
import ModalFieldRequiredError from './ModalFieldRequiredError';
import './TShirtManagement.css';
import './admin/FmModal.css';
import { useModalEscapeKey } from '../utils/useModalEscapeKey';

// Size labels as used in your order sheet
const SIZE_ORDER = ['WS', 'WM', 'WL', 'WXL', 'MS', 'MM', 'ML', 'MXL', 'MXXL'];

const NO_SHIRT_VALUE = TSHIRT_NO_ALLOCATION_VALUE;

/**
 * Controlled <select value> for roster allocation. Must match an <option> value.
 * Read changes with HTMLSelectElement.value (or selectedOptions[0].value), never innerText on SELECT.
 */
function allocatedSelectValueFromEffective(effectiveAllocated) {
  const n = normalizeSize(effectiveAllocated);
  if (n === NO_SHIRT_VALUE) return NO_SHIRT_VALUE;
  return n || '';
}

const ROSTER_FILTER_UNASSIGNED = '__unassigned__';

const PRIORITY_WAVES = {
  EARLY_FRESHLING: 1,
  LATE_FRESHLING: 2,
  MENTOR: 3,
  VOLUNTEER: 4,
};

const ALLOCATIONS_EXPORT_COLS = [
  { id: 'type', label: 'Type', default: true },
  { id: 'wave', label: 'Wave', default: true },
  { id: 'freshlingNum', label: 'Freshling #', default: true },
  { id: 'name', label: 'Name', default: true },
  { id: 'requested', label: 'Requested', default: true },
  { id: 'allocated', label: 'Allocated', default: true },
  { id: 'note', label: 'Note', default: true },
  { id: 'goodyBag', label: 'Goody bag', default: true },
];

const BAG_LIST_EXPORT_COLS = [
  { id: 'type', label: 'Type', default: true },
  { id: 'name', label: 'Name', default: true },
  { id: 'sizeDisplay', label: 'Size', default: true },
  { id: 'goodyBag', label: 'Goody bag', default: true },
];

const INVENTORY_EXPORT_COLS = [
  { id: 'size', label: 'Size', default: true },
  { id: 'ordered', label: 'Ordered', default: true },
  { id: 'freshling', label: 'Freshling', default: true },
  { id: 'mentor', label: 'Mentor', default: true },
  { id: 'volunteer', label: 'Volunteer', default: true },
  { id: 'requestedTotal', label: 'Requested total', default: true },
  { id: 'allocated', label: 'Allocated', default: true },
  { id: 'remaining', label: 'Gap', default: true },
];

const VOLUNTEER_ROSTER_EXPORT_COLS = [
  { id: 'name', label: 'Name', default: true },
  { id: 'email', label: 'Email', default: true },
  { id: 'notes', label: 'Notes', default: true },
  { id: 'requested', label: 'Requested size', default: false },
  { id: 'allocated', label: 'Allocated size', default: false },
];

const BAG_LIST_TYPE_ORDER = ['Freshling', 'Mentor', 'Volunteer'];

function getTypeLabelForPerson(p) {
  if (p.flyerType === 'freshling') return 'Freshling';
  if (p.flyerType === 'mentor') return 'Mentor';
  return 'Volunteer';
}

const TSHIRT_EXPORT_SUBTITLE = {
  allocations: 'Freshlings, mentors & volunteers',
  bagList: 'Bag list',
  inventory: 'Inventory by size',
  volunteerRoster: 'Volunteers',
};

function escapeHtmlCell(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function getColumnsForTshirtExport(ctx) {
  switch (ctx) {
    case 'allocations':
      return ALLOCATIONS_EXPORT_COLS;
    case 'bagList':
      return BAG_LIST_EXPORT_COLS;
    case 'inventory':
      return INVENTORY_EXPORT_COLS;
    case 'volunteerRoster':
      return VOLUNTEER_ROSTER_EXPORT_COLS;
    default:
      return [];
  }
}

/** Pending map entry (including explicit '') wins over Firestore original. */
function getEffectiveAllocated(p, pendingMap) {
  if (!p || !pendingMap) return '';
  if (!Object.prototype.hasOwnProperty.call(pendingMap, p.key)) {
    return normalizeSize(p.originalAllocated || '');
  }
  return normalizeSize(pendingMap[p.key] ?? '');
}

/** Empty effective allocation only — explicit NONE is excluded (decision already made). */
function isPersonTshirtTrulyUnallocated(p, pendingMap) {
  return !getEffectiveAllocated(p, pendingMap);
}

function buildInitialInventory(sizesField) {
  const base = {};
  SIZE_ORDER.forEach((s) => {
    base[s] = 0;
  });
  if (sizesField && typeof sizesField === 'object') {
    Object.entries(sizesField).forEach(([key, val]) => {
      const norm = normalizeSize(key);
      if (!norm) return;
      const n = Number(val);
      base[norm] = Number.isFinite(n) && n >= 0 ? n : 0;
    });
  }
  return base;
}

function TShirtManagement() {
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState('');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [currentEvent, setCurrentEvent] = useState(null);

  const [flyers, setFlyers] = useState([]);
  const volunteers = useMemo(
    () => flyers.filter((f) => f && f.type === 'volunteer'),
    [flyers]
  );
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState('');

  const [inventory, setInventory] = useState(() => buildInitialInventory(null));
  const [inventoryDirty, setInventoryDirty] = useState(false);
  const [inventorySaving, setInventorySaving] = useState(false);
  const [inventorySaveStatus, setInventorySaveStatus] = useState('');

  const [people, setPeople] = useState([]);
  const [pendingAllocatedByKey, setPendingAllocatedByKey] = useState({});
  const [allocationMetaByKey, setAllocationMetaByKey] = useState({});
  const [allocating, setAllocating] = useState(false);
  const [savingAllocations, setSavingAllocations] = useState(false);
  const [allocationsSaveStatus, setAllocationsSaveStatus] = useState('');

  const [volunteerForm, setVolunteerForm] = useState({
    name: '',
    email: '',
    tShirtRequested: '',
    notes: '',
  });
  const [volunteerSaving, setVolunteerSaving] = useState(false);
  const [bulkVolunteerInput, setBulkVolunteerInput] = useState('');
  const [bulkVolunteerStatus, setBulkVolunteerStatus] = useState('');
  const [bulkVolunteerSaving, setBulkVolunteerSaving] = useState(false);
  const [showBulkVolunteerImport, setShowBulkVolunteerImport] = useState(false);
  const [allocatedSizeFilter, setAllocatedSizeFilter] = useState('');
  const [savedOrderedTotal, setSavedOrderedTotal] = useState(0);
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [allocateFormInvalidNames, setAllocateFormInvalidNames] = useState(() => new Set());
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [tshirtTableExport, setTshirtTableExport] = useState(null); // { ctx, mode } | null
  const [tshirtTableExportCols, setTshirtTableExportCols] = useState(() => new Set());
  const [cutoffSettingsOpen, setCutoffSettingsOpen] = useState(false);
  const cutoffPopoverRef = useRef(null);
  const inventorySaveStatusTimerRef = useRef(null);
  const allocationsSaveStatusTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (inventorySaveStatusTimerRef.current) {
        clearTimeout(inventorySaveStatusTimerRef.current);
      }
      if (allocationsSaveStatusTimerRef.current) {
        clearTimeout(allocationsSaveStatusTimerRef.current);
      }
    };
  }, []);

  useModalEscapeKey(showAllocateModal, () => setShowAllocateModal(false), { enabled: !allocating });

  useEffect(() => {
    if (!showAllocateModal) setAllocateFormInvalidNames(new Set());
  }, [showAllocateModal]);

  useEffect(() => {
    let cancelled = false;
    async function loadEvents() {
      setEventsLoading(true);
      setEventsError('');
      try {
        const [snap, activeId] = await Promise.all([
          getDocs(collection(firestore, 'events')),
          getActiveEventId(),
        ]);
        if (cancelled) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEvents(list);
        if (list.length) {
          setSelectedEventId((prev) =>
            prev && list.some((e) => e.id === prev)
              ? prev
              : resolveAdminDefaultEventId({ eventIdParam: '', activeEventId: activeId, events: list })
          );
        }
      } catch (e) {
        if (!cancelled) setEventsError(e.message || 'Failed to load events.');
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    }
    loadEvents();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedEventId) {
      setCurrentEvent(null);
      setFlyers([]);
      setSavedOrderedTotal(0);
      return;
    }
    setDataLoading(true);
    setDataError('');
    (async () => {
      try {
        const [eventSnap, flyersSnap, inventorySnap] = await Promise.all([
          getDoc(doc(firestore, 'events', selectedEventId)),
          getDocs(
            query(
              collection(firestore, 'flyers'),
              where('eventId', '==', selectedEventId)
            )
          ),
          getDoc(doc(firestore, 'tshirtInventory', selectedEventId)),
        ]);
        setCurrentEvent(eventSnap.exists() ? { id: eventSnap.id, ...eventSnap.data() } : null);
        setFlyers(
          flyersSnap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }))
        );
        const invData = inventorySnap.exists() ? inventorySnap.data() : null;
        setInventory(buildInitialInventory(invData && invData.sizes));
        setSavedOrderedTotal(sumSavedInventoryOrdered(invData && invData.sizes));
        setInventoryDirty(false);
      } catch (e) {
        setDataError(e.message || 'Failed to load t-shirt data.');
      } finally {
        setDataLoading(false);
      }
    })();
  }, [selectedEventId]);

  useEffect(() => {
    if (!currentEvent) {
      setPeople([]);
      setPendingAllocatedByKey({});
      setAllocationMetaByKey({});
      return;
    }
    const cutoffMs = getCutoffMs(currentEvent);
    const activeFlyers = flyers.filter(
      (f) =>
        f &&
        (f.type === 'freshling' || f.type === 'mentor') &&
        !f.test &&
        !f.cancelled &&
        !isWithdrawnOrDuplicate(f)
    );
    const dedupedFlyers = dedupeFlyersByEmail(activeFlyers, isWithdrawnOrDuplicate);
    const earlyFreshlings = [];
    const lateFreshlings = [];
    const mentors = [];
    dedupedFlyers.forEach((f) => {
      if (f.type === 'mentor') {
        mentors.push(f);
      } else if (f.type === 'freshling') {
        const createdMs = getRegistrationDateMs(f);
        if (cutoffMs != null && createdMs != null && createdMs <= cutoffMs) {
          earlyFreshlings.push(f);
        } else {
          lateFreshlings.push(f);
        }
      }
    });
    const volunteerPool = flyers.filter(
      (f) =>
        f &&
        f.type === 'volunteer' &&
        !f.test &&
        !f.cancelled &&
        !isWithdrawnOrDuplicate(f)
    );
    const activeVolunteers = dedupeFlyersByEmail(volunteerPool, isWithdrawnOrDuplicate);
    const nextPeople = [];

    const pushFlyer = (f, wave) => {
      const key = `flyer:${f.id}`;
      const requestedSize = getRequestedSize(f);
      const allocatedSize = normalizeSize(f.tShirtAllocated);
      const displayName =
        (f.type === 'freshling' ? getFreshlingDisplayName(f) : getDisplayName(f)) ||
        f.name ||
        f.email ||
        '';
      const firstName = getFirstName(f) || (displayName.split(/\s+/)[0] || '');
      nextPeople.push({
        key,
        id: f.id,
        kind: 'flyer',
        flyerType: f.type,
        wave,
        displayName,
        firstName,
        email: f.email || '',
        requestedSize,
        originalAllocated: allocatedSize || '',
        team: f.team || '',
        sheetOrder: f.sheetOrder || '',
        goodyBag: f.type === 'volunteer' ? !!f.goodyBag : true,
      });
    };

    earlyFreshlings
      .slice()
      .sort((a, b) =>
        (getFreshlingDisplayName(a) || a.name || a.email || '').localeCompare(
          getFreshlingDisplayName(b) || b.name || b.email || '',
          undefined,
          { sensitivity: 'base' }
        )
      )
      .forEach((f) => pushFlyer(f, PRIORITY_WAVES.EARLY_FRESHLING));

    lateFreshlings
      .slice()
      .sort((a, b) =>
        (getFreshlingDisplayName(a) || a.name || a.email || '').localeCompare(
          getFreshlingDisplayName(b) || b.name || b.email || '',
          undefined,
          { sensitivity: 'base' }
        )
      )
      .forEach((f) => pushFlyer(f, PRIORITY_WAVES.LATE_FRESHLING));

    activeVolunteers
      .slice()
      .sort((a, b) =>
        (a.name || a.email || '').localeCompare(b.name || b.email || '', undefined, {
          sensitivity: 'base',
        })
      )
      .forEach((v) => pushFlyer(v, PRIORITY_WAVES.VOLUNTEER));

    mentors
      .filter((f) => !isWaitingMentor(f))
      .slice()
      .sort((a, b) =>
        (getDisplayName(a) || a.name || a.email || '').localeCompare(
          getDisplayName(b) || b.name || b.email || '',
          undefined,
          { sensitivity: 'base' }
        )
      )
      .forEach((f) => pushFlyer(f, PRIORITY_WAVES.MENTOR));

    const parseSheetOrderValue = (p) => {
      if (p.flyerType !== 'freshling') return Number.POSITIVE_INFINITY;
      const raw = (p.sheetOrder || '').toString().trim().toLowerCase();
      if (!raw || raw === 'x') return Number.POSITIVE_INFINITY;
      const n = parseInt(raw, 10);
      return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY - 1;
    };

    nextPeople.sort((a, b) => {
      if (a.wave !== b.wave) return a.wave - b.wave;
      const aOrder = parseSheetOrderValue(a);
      const bOrder = parseSheetOrderValue(b);
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' });
    });

    setPeople(nextPeople);
    // Flyers/volunteers often get new array references (e.g. editing a volunteer). Re-seeding pending
    // from scratch would drop every unsaved shirt allocation. Keep working values for roster keys
    // that still exist; only seed keys that are new to this session.
    setPendingAllocatedByKey((prevPending) => {
      const nextPending = {};
      nextPeople.forEach((p) => {
        if (Object.prototype.hasOwnProperty.call(prevPending, p.key)) {
          nextPending[p.key] = prevPending[p.key];
          return;
        }
        const effective = p.originalAllocated || p.requestedSize || '';
        if (effective) {
          nextPending[p.key] = effective;
        }
      });
      return nextPending;
    });
    setAllocationMetaByKey((prevMeta) => {
      const next = {};
      nextPeople.forEach((p) => {
        if (Object.prototype.hasOwnProperty.call(prevMeta, p.key)) {
          next[p.key] = prevMeta[p.key];
        }
      });
      return next;
    });
  }, [currentEvent, flyers]);

  const cutoffDisplay = useMemo(() => {
    if (!currentEvent || !currentEvent.tShirtPriorityCutoff) return 'Not set';
    const raw = currentEvent.tShirtPriorityCutoff;
    try {
      let d;
      if (typeof raw.toDate === 'function') {
        d = raw.toDate();
      } else if (typeof raw.seconds === 'number') {
        d = new Date(raw.seconds * 1000);
      } else {
        d = new Date(String(raw));
      }
      return d instanceof Date && Number.isFinite(d.getTime())
        ? d.toLocaleDateString()
        : String(raw);
    } catch {
      return String(raw);
    }
  }, [currentEvent]);

  const [cutoffInput, setCutoffInput] = useState('');
  const [cutoffSaving, setCutoffSaving] = useState(false);

  useEffect(() => {
    if (!currentEvent || !currentEvent.tShirtPriorityCutoff) {
      setCutoffInput('');
      return;
    }
    try {
      const raw = currentEvent.tShirtPriorityCutoff;
      let d;
      if (typeof raw.toDate === 'function') {
        d = raw.toDate();
      } else if (typeof raw.seconds === 'number') {
        d = new Date(raw.seconds * 1000);
      } else {
        d = new Date(String(raw));
      }
      if (!(d instanceof Date) || !Number.isFinite(d.getTime())) {
        setCutoffInput('');
        return;
      }
      const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
      setCutoffInput(local.toISOString().slice(0, 10));
    } catch {
      setCutoffInput('');
    }
  }, [currentEvent]);

  const handleSaveCutoff = async () => {
    if (!selectedEventId) return;
    setCutoffSaving(true);
    try {
      const updates = {};
      if (!cutoffInput) {
        updates.tShirtPriorityCutoff = null;
      } else {
        const d = new Date(cutoffInput + 'T12:00:00');
        if (!(d instanceof Date) || !Number.isFinite(d.getTime())) {
          setDataError('Invalid cutoff date.');
          setCutoffSaving(false);
          return;
        }
        updates.tShirtPriorityCutoff = d;
      }
      await updateDoc(doc(firestore, 'events', selectedEventId), updates);
      setCurrentEvent((prev) =>
        prev && prev.id === selectedEventId
          ? { ...prev, ...updates }
          : prev
      );
      setCutoffSettingsOpen(false);
    } catch (e) {
      setDataError(e.message || 'Failed to save cutoff date.');
    } finally {
      setCutoffSaving(false);
    }
  };

  useEffect(() => {
    if (!cutoffSettingsOpen) return;
    const onDocMouseDown = (e) => {
      if (cutoffPopoverRef.current && !cutoffPopoverRef.current.contains(e.target)) {
        setCutoffSettingsOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setCutoffSettingsOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [cutoffSettingsOpen]);

  const allocatedCounts = useMemo(() => {
    const counts = {};
    SIZE_ORDER.forEach((s) => {
      counts[s] = 0;
    });
    people.forEach((p) => {
      const size = getEffectiveAllocated(p, pendingAllocatedByKey);
      if (size === NO_SHIRT_VALUE) return;
      if (!size) return;
      if (!counts[size] && counts[size] !== 0) {
        counts[size] = 0;
      }
      counts[size] += 1;
    });
    return counts;
  }, [people, pendingAllocatedByKey]);

  const remainingInventory = useMemo(() => {
    const remaining = {};
    SIZE_ORDER.forEach((s) => {
      const avail = Number(inventory[s] || 0);
      const used = Number(allocatedCounts[s] || 0);
      remaining[s] = avail - used;
    });
    return remaining;
  }, [inventory, allocatedCounts]);

  const breakdownBySize = useMemo(() => {
    const map = {};
    SIZE_ORDER.forEach((s) => {
      map[s] = {
        freshlings: 0,
        mentorsAccepted: 0,
        volunteers: 0,
      };
    });
    const ensure = (size) => {
      const s = normalizeSize(size);
      if (!s) return null;
      if (!map[s]) {
        map[s] = {
          freshlings: 0,
          mentorsAccepted: 0,
          volunteers: 0,
        };
      }
      return map[s];
    };
    const activeFlyersForBreakdown = flyers.filter(
      (f) =>
        f &&
        (f.type === 'freshling' || f.type === 'mentor') &&
        !f.test &&
        !f.cancelled &&
        !isWithdrawnOrDuplicate(f)
    );
    const dedupedForBreakdown = dedupeFlyersByEmail(
      activeFlyersForBreakdown,
      isWithdrawnOrDuplicate
    );
    dedupedForBreakdown.forEach((f) => {
      if (!f || (f.type !== 'freshling' && f.type !== 'mentor')) return;
      const bucket = ensure(getRequestedSize(f));
      if (!bucket) return;
      if (f.type === 'freshling') {
        bucket.freshlings += 1;
      } else if (f.type === 'mentor' && !isWaitingMentor(f)) {
        bucket.mentorsAccepted += 1;
      }
    });
    dedupeFlyersByEmail(
      flyers.filter(
        (f) => f && f.type === 'volunteer' && !f.test && !f.cancelled && !isWithdrawnOrDuplicate(f)
      ),
      isWithdrawnOrDuplicate
    ).forEach((v) => {
      const bucket = ensure(getRequestedSize(v));
      if (!bucket) return;
      bucket.volunteers += 1;
    });
    return map;
  }, [flyers]);

  /** One row per size: demand columns + gap = ordered − currently allocated (live with roster). */
  const allocationDemandBySize = useMemo(() => {
    return SIZE_ORDER.map((size) => {
      const fr = breakdownBySize[size]?.freshlings ?? 0;
      const men = breakdownBySize[size]?.mentorsAccepted ?? 0;
      const vol = breakdownBySize[size]?.volunteers ?? 0;
      const totalReq = fr + men + vol;
      const ordered = Number(inventory[size] || 0);
      const gap = remainingInventory[size] ?? 0;
      return {
        size,
        ordered,
        freshlings: fr,
        mentors: men,
        volunteers: vol,
        totalRequested: totalReq,
        gap,
      };
    });
  }, [breakdownBySize, inventory, remainingInventory]);

  const shortfallBarItems = useMemo(
    () => allocationDemandBySize.filter((row) => row.gap < 0),
    [allocationDemandBySize]
  );

  const rosterSortedPeople = useMemo(() => {
    const typeOrder = (p) => {
      const i = BAG_LIST_TYPE_ORDER.indexOf(getTypeLabelForPerson(p));
      return i >= 0 ? i : 99;
    };
    return [...people].sort((a, b) => {
      if (typeOrder(a) !== typeOrder(b)) return typeOrder(a) - typeOrder(b);
      const firstA = (a.firstName || a.displayName || '').toLowerCase();
      const firstB = (b.firstName || b.displayName || '').toLowerCase();
      const cmp = firstA.localeCompare(firstB, undefined, { sensitivity: 'base' });
      if (cmp !== 0) return cmp;
      return (a.displayName || '').localeCompare(b.displayName || '', undefined, {
        sensitivity: 'base',
      });
    });
  }, [people]);

  const freshlingsWithNoSize = useMemo(
    () =>
      people.filter(
        (p) =>
          p.flyerType === 'freshling' &&
          !(p.requestedSize || '').toString().trim()
      ),
    [people]
  );

  const unallocatedPendingCount = useMemo(
    () => people.filter((p) => isPersonTshirtTrulyUnallocated(p, pendingAllocatedByKey)).length,
    [people, pendingAllocatedByKey]
  );

  const showTshirtUnallocatedWarning =
    savedOrderedTotal > 0 && unallocatedPendingCount > 0;

  const handleInventoryChange = (size, value) => {
    const n = Number(value);
    setInventory((prev) => ({
      ...prev,
      [size]: Number.isFinite(n) && n >= 0 ? n : 0,
    }));
    setInventoryDirty(true);
  };

  const handleSaveInventory = async () => {
    if (!selectedEventId) return;
    setInventorySaving(true);
    setInventorySaveStatus('');
    try {
      const sizes = {};
      SIZE_ORDER.forEach((s) => {
        const n = Number(inventory[s] || 0);
        if (Number.isFinite(n) && n > 0) {
          sizes[s] = n;
        }
      });
      await setDoc(
        doc(firestore, 'tshirtInventory', selectedEventId),
        {
          eventId: selectedEventId,
          sizes,
          lastUpdated: serverTimestamp(),
        },
        { merge: true }
      );
      setInventoryDirty(false);
      setSavedOrderedTotal(sumSavedInventoryOrdered(sizes));
      setInventorySaveStatus('Saved ✓');
      if (inventorySaveStatusTimerRef.current) {
        clearTimeout(inventorySaveStatusTimerRef.current);
      }
      inventorySaveStatusTimerRef.current = setTimeout(() => {
        setInventorySaveStatus('');
      }, 2200);
    } catch (e) {
      setDataError(e.message || 'Failed to save inventory.');
      setInventorySaveStatus('Save failed');
    } finally {
      setInventorySaving(false);
    }
  };

  /** Persist roster shirt sizes using the given pending map (defaults to current state). */
  const flushAllocationsToFirestore = async (pendingOverride) => {
    const map = pendingOverride ?? pendingAllocatedByKey;
    if (!people.length) return;
    const flyerUpdates = [];
    people.forEach((p) => {
      const finalSize = getEffectiveAllocated(p, map);
      const original = normalizeSize(p.originalAllocated || '');
      if (finalSize === original) return;
      if (p.kind === 'flyer') {
        flyerUpdates.push({ id: p.id, size: finalSize });
      }
    });
    if (!flyerUpdates.length) return;
    const batch = writeBatch(firestore);
    flyerUpdates.forEach((u) => {
      batch.update(doc(firestore, 'flyers', u.id), {
        tShirtAllocated: u.size || null,
        tShirtRequested: getRequestedSize(flyers.find((f) => f.id === u.id)) || null,
      });
    });
    await batch.commit();
    if (flyerUpdates.length) {
      setFlyers((prev) =>
        prev.map((f) => {
          const u = flyerUpdates.find((x) => x.id === f.id);
          if (!u) return f;
          return {
            ...f,
            tShirtAllocated: u.size || null,
            tShirtRequested: getRequestedSize(f) || null,
          };
        })
      );
    }
  };

  const handleSaveAllocations = async () => {
    if (!people.length) return;
    setSavingAllocations(true);
    setAllocationsSaveStatus('');
    try {
      await flushAllocationsToFirestore();
      setAllocationsSaveStatus('Saved ✓');
      if (allocationsSaveStatusTimerRef.current) {
        clearTimeout(allocationsSaveStatusTimerRef.current);
      }
      allocationsSaveStatusTimerRef.current = setTimeout(() => {
        setAllocationsSaveStatus('');
      }, 2200);
    } catch (e) {
      setDataError(e.message || 'Failed to save allocations.');
      setAllocationsSaveStatus('Save failed');
    } finally {
      setSavingAllocations(false);
    }
  };

  const allocateNow = async () => {
    if (!people.length) return false;
    setAllocating(true);
    try {
      const nextPending = { ...pendingAllocatedByKey };
      const nextMeta = { ...allocationMetaByKey };
      people.forEach((p) => {
        const key = p.key;
        const cur = getEffectiveAllocated(p, nextPending);
        if (cur !== '') return;
        const req = normalizeSize(p.requestedSize || '');
        if (!req) return;
        nextPending[key] = req;
        const requested = p.requestedSize || '';
        nextMeta[key] = {
          autoAssigned: true,
          changed: false,
          requested: requested || '',
          allocated: req,
        };
      });
      setPendingAllocatedByKey(nextPending);
      setAllocationMetaByKey(nextMeta);
      await flushAllocationsToFirestore(nextPending);
      return true;
    } catch (e) {
      setDataError(e.message || 'Failed to save allocations.');
      return false;
    } finally {
      setAllocating(false);
    }
  };

  const openResetModal = () => {
    setResetModalOpen(true);
  };

  const closeResetModal = () => {
    setResetModalOpen(false);
  };

  useModalEscapeKey(resetModalOpen, closeResetModal);

  const applyResetAllPending = async () => {
    if (!people.length) {
      closeResetModal();
      return;
    }
    const next = {};
    people.forEach((p) => {
      next[p.key] = '';
    });
    setPendingAllocatedByKey(next);
    setAllocationMetaByKey({});
    closeResetModal();
    try {
      await flushAllocationsToFirestore(next);
    } catch (e) {
      setDataError(e.message || 'Failed to save shirt allocation.');
    }
  };

  const handleManualAllocatedChange = (key, valueFromSelect) => {
    const size = normalizeSize(valueFromSelect);
    const mapAfter = { ...pendingAllocatedByKey, [key]: size };
    setPendingAllocatedByKey((prev) => ({
      ...prev,
      [key]: size,
    }));
    setAllocationMetaByKey((prev) => {
      const p = people.find((x) => x.key === key);
      if (!p) return prev;
      const requested = p.requestedSize || '';
      const changed =
        size &&
        requested &&
        normalizeSize(size) !== normalizeSize(requested);
      return {
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          autoAssigned: false,
          changed,
          requested: requested || '',
          allocated: size || '',
        },
      };
    });
    flushAllocationsToFirestore(mapAfter).catch((e) => {
      setDataError(e.message || 'Failed to save shirt allocation.');
    });
  };

  const buildSwapNoteText = (p, allocated, requested) => {
    if (!allocated || allocated === NO_SHIRT_VALUE) return '';
    if (!requested) return '';
    if (normalizeSize(allocated) === normalizeSize(requested)) return '';
    return `${requested} → ${allocated}`;
  };

  const buildBagListSorted = () => {
    const typeOrder = (p) => {
      const i = BAG_LIST_TYPE_ORDER.indexOf(getTypeLabelForPerson(p));
      return i >= 0 ? i : 99;
    };
    return [...people]
      .sort((a, b) => {
        if (typeOrder(a) !== typeOrder(b)) return typeOrder(a) - typeOrder(b);
        const firstA = (a.firstName || a.displayName || '').toLowerCase();
        const firstB = (b.firstName || b.displayName || '').toLowerCase();
        const cmp = firstA.localeCompare(firstB, undefined, { sensitivity: 'base' });
        if (cmp !== 0) return cmp;
        return (a.displayName || '').localeCompare(b.displayName || '', undefined, {
          sensitivity: 'base',
        });
      })
      .map((p) => {
        const allocatedRaw = getEffectiveAllocated(p, pendingAllocatedByKey);
        const requested = normalizeSize(p.requestedSize || '');
        let sizeDisplay;
        if (allocatedRaw === NO_SHIRT_VALUE) {
          sizeDisplay = 'None (no shirt)';
        } else if (allocatedRaw) {
          sizeDisplay =
            requested && requested !== allocatedRaw
              ? `${allocatedRaw} (requested ${requested})`
              : allocatedRaw;
        } else {
          sizeDisplay = 'Not set';
        }
        return {
          type: getTypeLabelForPerson(p),
          name: p.displayName || '',
          sizeDisplay,
          goodyBag:
            p.flyerType === 'volunteer'
              ? !!p.goodyBag
              : p.flyerType === 'freshling' || p.flyerType === 'mentor',
          isVolunteer: p.flyerType === 'volunteer',
        };
      });
  };

  const getRowsForTshirtExport = (ctx) => {
    switch (ctx) {
      case 'allocations': {
        const allocationPeople = people.filter(
          (p) =>
            p.flyerType === 'freshling' ||
            p.flyerType === 'mentor' ||
            p.flyerType === 'volunteer'
        );
        return allocationPeople.map((p) => {
          const requested = p.requestedSize || '';
          const allocatedRaw = getEffectiveAllocated(p, pendingAllocatedByKey);
          const allocatedForLogic =
            allocatedRaw === NO_SHIRT_VALUE ? '' : allocatedRaw;
          let allocatedLabel;
          if (allocatedRaw === NO_SHIRT_VALUE) {
            allocatedLabel = 'None (no shirt)';
          } else if (allocatedRaw) {
            allocatedLabel = allocatedRaw;
          } else {
            allocatedLabel = 'Not set';
          }
          const meta = allocationMetaByKey[p.key] || {};
          const changed =
            allocatedForLogic &&
            requested &&
            normalizeSize(allocatedForLogic) !== normalizeSize(requested);
          const waveLabel =
            p.flyerType === 'volunteer'
              ? 'Volunteer'
              : p.wave === PRIORITY_WAVES.EARLY_FRESHLING
              ? 'Early freshling'
              : p.wave === PRIORITY_WAVES.LATE_FRESHLING
              ? 'Freshling'
              : 'Mentor';
          const typeLabel =
            p.flyerType === 'freshling'
              ? 'Freshling'
              : p.flyerType === 'mentor'
              ? 'Mentor'
              : 'Volunteer';
          const freshlingNum =
            p.flyerType === 'freshling' ? (p.sheetOrder || '').toString().trim() || '—' : '—';
          const note =
            changed && requested && allocatedForLogic
              ? `Requested ${requested}, got ${allocatedForLogic}`
              : meta.autoAssigned
              ? 'Auto-assigned'
              : '';
          const goodyBagText =
            p.flyerType === 'volunteer'
              ? p.goodyBag
                ? 'Yes'
                : ''
              : p.flyerType === 'freshling' || p.flyerType === 'mentor'
                ? 'Yes'
                : '';
          return {
            type: typeLabel,
            wave: waveLabel,
            freshlingNum,
            name: p.displayName || '',
            requested: requested || '—',
            allocated: allocatedLabel,
            note,
            goodyBag: goodyBagText,
          };
        });
      }
      case 'bagList':
        return buildBagListSorted().map((r) => ({
          type: r.type,
          name: r.name,
          sizeDisplay: r.sizeDisplay,
          goodyBag: r.goodyBag ? 'Yes' : '',
        }));
      case 'inventory':
        return SIZE_ORDER.map((size) => {
          const fr = breakdownBySize[size]?.freshlings ?? 0;
          const men = breakdownBySize[size]?.mentorsAccepted ?? 0;
          const vol = breakdownBySize[size]?.volunteers ?? 0;
          return {
            size,
            ordered: String(inventory[size] ?? 0),
            freshling: String(fr),
            mentor: String(men),
            volunteer: String(vol),
            requestedTotal: String(fr + men + vol),
            allocated: String(allocatedCounts[size] ?? 0),
            remaining: String(remainingInventory[size] ?? 0),
          };
        });
      case 'volunteerRoster':
        return volunteers
          .filter((v) => !v.cancelled)
          .slice()
          .sort((a, b) =>
            (a.name || a.email || '').localeCompare(b.name || b.email || '', undefined, {
              sensitivity: 'base',
            })
          )
          .map((v) => {
            const a = normalizeSize(v.tShirtAllocated || '');
            let allocatedLabel;
            if (a === NO_SHIRT_VALUE) allocatedLabel = 'None (no shirt)';
            else if (a) allocatedLabel = a;
            else allocatedLabel = 'Not set';
            return {
              name: v.name || '',
              email: v.email || '',
              notes: v.notes || '',
              requested: normalizeSize(v.tShirtRequested || '') || '—',
              allocated: allocatedLabel,
            };
          });
      default:
        return [];
    }
  };

  useEffect(() => {
    if (!tshirtTableExport) return;
    const cols = getColumnsForTshirtExport(tshirtTableExport.ctx);
    setTshirtTableExportCols(new Set(cols.filter((c) => c.default).map((c) => c.id)));
  }, [tshirtTableExport]);

  const openTshirtTableExport = (ctx, mode) => {
    const rows = getRowsForTshirtExport(ctx);
    if (!rows.length) {
      window.alert('Nothing to export for this view.');
      return;
    }
    setTshirtTableExport({ ctx, mode });
  };

  const resetTshirtTableExportCols = () => {
    if (!tshirtTableExport) return;
    const cols = getColumnsForTshirtExport(tshirtTableExport.ctx);
    setTshirtTableExportCols(new Set(cols.filter((c) => c.default).map((c) => c.id)));
  };

  const runTshirtTableExport = (selectedColumnIds) => {
    if (!tshirtTableExport) return;
    const { ctx, mode } = tshirtTableExport;
    const allCols = getColumnsForTshirtExport(ctx);
    const colSet =
      Array.isArray(selectedColumnIds) && selectedColumnIds.length > 0
        ? new Set(selectedColumnIds)
        : tshirtTableExportCols;
    const active = allCols.filter((c) => colSet.has(c.id));
    if (!active.length) return;

    const eventTitle = currentEvent?.title || selectedEventId || 'Event';

    if (ctx === 'bagList') {
      const rows = buildBagListSorted();
      const rowRecords = rows.map((r) => ({
        type: r.type,
        name: r.name,
        sizeDisplay: r.sizeDisplay,
        goodyBag: r.goodyBag ? 'Yes' : '',
      }));
      const colMeta = active.map((c) => ({ id: c.id, header: c.label }));
      if (mode === 'copy') {
        const slim = rowRecords.map((rec) => {
          const o = {};
          active.forEach((c) => {
            o[c.id] = rec[c.id] ?? '';
          });
          return o;
        });
        copyText(buildTsvTable(colMeta, slim));
      } else {
        const typeOrder = BAG_LIST_TYPE_ORDER;
        const sections = typeOrder
          .map((type) => ({ type, rows: rows.filter((r) => r.type === type) }))
          .filter((s) => s.rows.length > 0);
        const headerCells = active.map((c) => `<th>${escapeHtmlCell(c.label)}</th>`).join('');
        const zebra = (rowHtml, index) =>
          `<tr class="${index % 2 === 0 ? 'bag-list-row-even' : 'bag-list-row-odd'}">${rowHtml}</tr>`;
        const sectionHtml = sections
          .map((s) => {
            const bodyRows = s.rows
              .map((r, i) => {
                const rec = {
                  type: r.type,
                  name: r.name,
                  sizeDisplay: r.sizeDisplay,
                  goodyBag: r.goodyBag ? 'Yes' : '',
                };
                const cells = active
                  .map((c) => {
                    let v = rec[c.id] ?? '';
                    if (c.id === 'goodyBag') {
                      v = r.goodyBag ? '✓' : '☐';
                    }
                    return `<td>${escapeHtmlCell(v)}</td>`;
                  })
                  .join('');
                return zebra(cells, i);
              })
              .join('');
            return `<div class="bag-list-page"><h2>${escapeHtmlCell(s.type)}</h2><table class="bag-list-table"><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table></div>`;
          })
          .join('');
        const extraCss = `
      body { font-family: sans-serif; padding: 1rem; }
      .bag-list-page { page-break-before: always; }
      .bag-list-page:first-child { page-break-before: auto; }
      .bag-list-table { border-collapse: collapse; width: 100%; margin-bottom: 1rem; }
      .bag-list-table th, .bag-list-table td { border: 1px solid #333; padding: 0.35rem 0.5rem; text-align: left; }
      .bag-list-table th { background: #eee; }
      .bag-list-table .bag-list-row-odd { background: #f5f5f5; }
      .bag-list-table .bag-list-row-even { background: #fff; }
      .bag-list-page h2 { margin-top: 0; }
      @media print {
        .bag-list-page { page-break-before: always; }
        .bag-list-page:first-child { page-break-before: auto; }
        .bag-list-table .bag-list-row-odd, .bag-list-table .bag-list-row-even {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `;
        const bodyHtml = `<h1>${escapeHtmlCell(`Bag list – ${eventTitle}`)}</h1>
<p>${escapeHtmlCell(
          'Sorted by type, then first name. Columns match your selection. Size shows allocated; if different from requested, shown as "Size (requested X)".'
        )}</p>
${sectionHtml}`;
        const ok = openPrintWindowOptions({
          title: 'Bag list',
          bodyHtml,
          extraCss,
          autoPrint: true,
        });
        if (!ok) {
          window.alert('Popup blocked. Please allow popups and try again.');
        }
      }
      setTshirtTableExport(null);
      return;
    }

    const rows = getRowsForTshirtExport(ctx);
    const colMeta = active.map((c) => ({ id: c.id, header: c.label }));
    const rowRecords = rows.map((r) => {
      const o = {};
      active.forEach((c) => {
        o[c.id] = r[c.id] ?? '';
      });
      return o;
    });

    if (mode === 'copy') {
      copyText(buildTsvTable(colMeta, rowRecords));
    } else {
      const sub = TSHIRT_EXPORT_SUBTITLE[ctx] || 'Table';
      const headerCells = active.map((c) => `<th>${escapeHtmlCell(c.label)}</th>`).join('');
      const rowsHtml = rowRecords
        .map((rec) => {
          const cells = active.map((c) => `<td>${escapeHtmlCell(rec[c.id])}</td>`).join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');
      const bodyHtml = `<main style="padding:8px 12px"><h1>${escapeHtmlCell(eventTitle)}</h1><p>${escapeHtmlCell(
        sub
      )}</p><p>${rowRecords.length} row(s)</p><table><thead><tr>${headerCells}</tr></thead><tbody>${rowsHtml}</tbody></table></main>`;
      openPrintWindow(`${eventTitle} — ${sub}`, bodyHtml, getOperationalTablePrintCss());
    }
    setTshirtTableExport(null);
  };

  const handleVolunteerFieldChange = (field, value) => {
    setVolunteerForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const parseBulkVolunteerInput = (text) => {
    if (!text) return [];
    const lines = text.split(/\r?\n/);
    const rows = [];
    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed || !trimmed.includes('|')) return;
      if (trimmed.startsWith('| ---') || trimmed.startsWith('|---')) return;
      const parts = trimmed.replace(/^\|/, '').replace(/\|$/, '').split('|').map((p) => p.trim());
      if (parts.length < 3) return;
      const [nameCell, emailCell, sizeCell] = parts;
      if (!nameCell && !emailCell && !sizeCell) return;
      let email = '';
      const mailtoIdx = emailCell.toLowerCase().indexOf('mailto:');
      if (mailtoIdx !== -1) {
        const after = emailCell.slice(mailtoIdx + 7);
        const endIdx = after.indexOf(')');
        email = (endIdx === -1 ? after : after.slice(0, endIdx)).trim();
      } else if (emailCell.includes('@')) {
        email = emailCell.replace(/^\[|\]$/g, '').trim();
      }
      const name = nameCell.trim();
      const size = sizeCell.trim();
      rows.push({ name, email, size });
    });
    return rows;
  };

  const handleBulkImportVolunteers = async () => {
    if (!selectedEventId) return;
    const parsed = parseBulkVolunteerInput(bulkVolunteerInput);
    if (!parsed.length) {
      setBulkVolunteerStatus('Nothing to import from the pasted text.');
      return;
    }
    setBulkVolunteerSaving(true);
    setBulkVolunteerStatus('');
    try {
      const existingByKey = new Map();
      volunteers.forEach((v) => {
        const emailKey = (v.email || '').toLowerCase().trim();
        const nameKey = (v.name || '').toLowerCase().trim();
        if (emailKey) {
          existingByKey.set(`email:${emailKey}`, v);
        } else if (nameKey) {
          existingByKey.set(`name:${nameKey}`, v);
        }
      });

      const batch = writeBatch(firestore);
      let imported = 0;
      let updated = 0;

      parsed.forEach((row) => {
        const name = (row.name || '').trim();
        const email = (row.email || '').trim();
        const sizeNorm = normalizeSize(row.size || '');
        if (!name && !email && !sizeNorm) return;
        const emailKey = email.toLowerCase();
        const nameKey = name.toLowerCase();
        const key = emailKey ? `email:${emailKey}` : nameKey ? `name:${nameKey}` : null;
        if (!key) return;
        const existing = existingByKey.get(key);
        if (existing) {
          batch.update(doc(firestore, 'flyers', existing.id), {
            name: name || existing.name || (email || existing.email || 'Volunteer'),
            email: email || existing.email || null,
            tShirtRequested: sizeNorm || existing.tShirtRequested || null,
          });
          updated += 1;
        } else {
          batch.set(doc(collection(firestore, 'flyers')), {
            eventId: selectedEventId,
            type: 'volunteer',
            name: name || (email || 'Volunteer'),
            email: email || null,
            role: 'volunteer',
            tShirtRequested: sizeNorm || null,
            tShirtAllocated: null,
            notes: null,
            goodyBag: false,
            cancelled: false,
            test: false,
            jumpNumber: null,
            createdAt: serverTimestamp(),
          });
          imported += 1;
        }
      });

      if (imported === 0 && updated === 0) {
        setBulkVolunteerStatus('No changes to apply from the pasted text.');
        setBulkVolunteerSaving(false);
        return;
      }

      await batch.commit();

      const snap = await getDocs(
        query(collection(firestore, 'flyers'), where('eventId', '==', selectedEventId))
      );
      setFlyers(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
      setBulkVolunteerStatus(`Imported ${imported} and updated ${updated} volunteers.`);
    } catch (e) {
      setBulkVolunteerStatus(e.message || 'Failed to import volunteers.');
    } finally {
      setBulkVolunteerSaving(false);
    }
  };

  const handleAddVolunteer = async (e) => {
    e.preventDefault();
    if (!selectedEventId) return;
    const name = (volunteerForm.name || '').trim();
    const email = (volunteerForm.email || '').trim();
    const requested = normalizeSize(volunteerForm.tShirtRequested);
    const notes = (volunteerForm.notes || '').trim();
    if (!name && !email) return;
    setVolunteerSaving(true);
    try {
      await addDoc(collection(firestore, 'flyers'), {
        eventId: selectedEventId,
        type: 'volunteer',
        name: name || (email || 'Volunteer'),
        email: email || null,
        role: 'volunteer',
        tShirtRequested: requested || null,
        tShirtAllocated: null,
        notes: notes || null,
        goodyBag: false,
        cancelled: false,
        test: false,
        jumpNumber: null,
        createdAt: serverTimestamp(),
      });
      const snap = await getDocs(
        query(collection(firestore, 'flyers'), where('eventId', '==', selectedEventId))
      );
      setFlyers(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
      setVolunteerForm({
        name: '',
        email: '',
        tShirtRequested: '',
        notes: '',
      });
    } catch (err) {
      setDataError(err.message || 'Failed to add volunteer.');
    } finally {
      setVolunteerSaving(false);
    }
  };

  const handleUpdateVolunteer = async (volunteer) => {
    const name = (volunteer.name || '').trim();
    const email = (volunteer.email || '').trim();
    const requested = normalizeSize(volunteer.tShirtRequested);
    const allocated = normalizeSize(volunteer.tShirtAllocated);
    const notes = (volunteer.notes || '').trim();
    try {
      await updateDoc(doc(firestore, 'flyers', volunteer.id), {
        name: name || (email || 'Volunteer'),
        email: email || null,
        tShirtRequested: requested || null,
        tShirtAllocated: allocated || null,
        notes: notes || null,
      });
      setFlyers((prev) =>
        prev.map((f) =>
          f.id === volunteer.id
            ? {
                ...f,
                name: name || (email || 'Volunteer'),
                email: email || null,
                tShirtRequested: requested || null,
                tShirtAllocated: allocated || null,
                notes: notes || null,
              }
            : f
        )
      );
    } catch (err) {
      setDataError(err.message || 'Failed to update volunteer.');
    }
  };

  const handleDeleteVolunteer = async (id) => {
    if (!window.confirm('Delete this volunteer?')) return;
    try {
      await updateDoc(doc(firestore, 'flyers', id), {
        cancelled: true,
      });
      setFlyers((prev) =>
        prev.map((f) => (f.id === id ? { ...f, cancelled: true } : f))
      );
    } catch (err) {
      setDataError(err.message || 'Failed to delete volunteer.');
    }
  };

  const handleVolunteerGoodyBagChange = async (volunteerId, checked) => {
    try {
      await updateDoc(doc(firestore, 'flyers', volunteerId), {
        goodyBag: !!checked,
      });
      setFlyers((prev) =>
        prev.map((f) =>
          f.id === volunteerId ? { ...f, goodyBag: !!checked } : f
        )
      );
    } catch (err) {
      setDataError(err.message || 'Failed to update goody bag.');
    }
  };

  if (eventsLoading && !events.length) {
    return (
      <div className="tshirt-management">
        <h2>T-Shirt Management</h2>
        <p>Loading events…</p>
      </div>
    );
  }

  if (!events.length) {
    return (
      <div className="tshirt-management">
        <h2>T-Shirt Management</h2>
        <p>No events found. Create an event first.</p>
      </div>
    );
  }

  return (
    <div className="tshirt-management">
      <div className="tshirt-header">
        <div>
          <h2>T-Shirt Management</h2>
          {eventsError && <p className="error">{eventsError}</p>}
          {dataError && <p className="error">{dataError}</p>}
        </div>
        <div className="tshirt-header-controls">
          <label>
            Event:{' '}
            <select
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
            >
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.title}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {showTshirtUnallocatedWarning && (
        <div className="tshirt-page-alert" role="alert">
          <strong>Not allocated yet:</strong> {unallocatedPendingCount} roster
          {unallocatedPendingCount !== 1 ? ' members' : ' member'} still have{' '}
          <strong>no allocation set</strong> (dropdown &quot;Not set&quot;) while inventory is saved for this
          event. People marked <strong>None (no shirt)</strong> are excluded. Pick a size in the roster —
          each change saves to Firestore automatically.
        </div>
      )}

      <div className="tshirt-body">
        <div className="tshirt-section tshirt-section--inventory">
          <div className="tshirt-section-head">
            <div className="tshirt-section-title-cluster">
              <h3 className="tshirt-section-title">Inventory &amp; demand</h3>
              {currentEvent && (
                <div className="tshirt-cutoff-settings-wrap" ref={cutoffPopoverRef}>
                  <button
                    type="button"
                    className="tshirt-gear-btn"
                    aria-expanded={cutoffSettingsOpen}
                    aria-haspopup="dialog"
                    aria-label="Early freshling cutoff date"
                    title={`Cutoff: ${cutoffDisplay}`}
                    onClick={() => setCutoffSettingsOpen((o) => !o)}
                  >
                    <span aria-hidden>⚙</span>
                  </button>
                  {cutoffSettingsOpen && (
                    <div
                      className="tshirt-cutoff-popover fm-modal"
                      role="dialog"
                      aria-labelledby="tshirt-cutoff-popover-title"
                      aria-modal="true"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="fm-modal-header">
                        <h2 className="fm-modal-title" id="tshirt-cutoff-popover-title">
                          Early freshling cutoff
                        </h2>
                        <button
                          type="button"
                          className="fm-modal-close"
                          onClick={() => setCutoffSettingsOpen(false)}
                          aria-label="Close"
                        >
                          ✕
                        </button>
                      </div>
                      <div className="fm-modal-body">
                        <p className="tshirt-cutoff-popover-hint">
                          Registrations on or before this date count as early freshlings for allocation
                          priority.
                        </p>
                        <label className="tshirt-cutoff-popover-label">
                          Cutoff date
                          <input
                            type="date"
                            value={cutoffInput}
                            onChange={(e) => setCutoffInput(e.target.value)}
                          />
                        </label>
                      </div>
                      <div className="fm-modal-footer">
                        <button
                          type="button"
                          className="fm-modal-btn fm-modal-btn-cancel"
                          onClick={() => setCutoffSettingsOpen(false)}
                          disabled={cutoffSaving}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="fm-modal-btn fm-modal-btn-primary"
                          onClick={handleSaveCutoff}
                          disabled={cutoffSaving}
                        >
                          {cutoffSaving ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="tshirt-export-cluster">
              <button
                type="button"
                className="tshirt-btn tshirt-btn--ghost"
                onClick={() => openTshirtTableExport('inventory', 'print')}
                disabled={dataLoading}
              >
                Print
              </button>
              <AdminCopyMenu
                ariaLabel="Copy inventory table"
                disabled={dataLoading}
                items={[
                  {
                    label: 'Copy…',
                    onSelect: () => openTshirtTableExport('inventory', 'copy'),
                    showCopiedFeedback: false,
                  },
                ]}
              />
            </div>
          </div>
          <p className="tshirt-section-lede">
            Ordered counts vs registration demand; <strong>Gap</strong> is ordered minus what&apos;s
            currently allocated on the roster (updates live). Save ordered counts when finished.
            <strong> Allocate Now</strong> fills only unassigned rows with each person&apos;s requested
            size (and saves). <strong>Reset allocations</strong> clears everyone (confirm twice) and saves.
            Roster dropdown changes save automatically.
          </p>
          {freshlingsWithNoSize.length > 0 && (
            <div className="tshirt-freshlings-no-size">
              <strong>
                {freshlingsWithNoSize.length} freshling
                {freshlingsWithNoSize.length !== 1 ? 's' : ''} with no requested size
              </strong>
              <p>Set a size in Flyer Management or pick one in the roster.</p>
              <ul>
                {freshlingsWithNoSize.map((p) => (
                  <li key={p.key}>
                    {p.displayName}
                    {p.email ? ` (${p.email})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="tshirt-inventory-table-wrap">
            <table className="tshirt-table tshirt-table--combined">
              <thead>
                <tr>
                  <th>Size</th>
                  <th>Ordered</th>
                  <th>Freshlings req.</th>
                  <th>Mentors req.</th>
                  <th>Volunteers req.</th>
                  <th>Total requested</th>
                  <th>Gap</th>
                </tr>
              </thead>
              <tbody>
                {allocationDemandBySize.map((row) => (
                  <tr
                    key={row.size}
                    className={row.gap < 0 ? 'tshirt-row-gap-short' : undefined}
                  >
                    <td>{row.size}</td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        className="tshirt-inventory-input"
                        value={inventory[row.size] ?? 0}
                        onChange={(e) => handleInventoryChange(row.size, e.target.value)}
                      />
                    </td>
                    <td>{row.freshlings}</td>
                    <td>{row.mentors}</td>
                    <td>{row.volunteers}</td>
                    <td>{row.totalRequested}</td>
                    <td
                      className={
                        row.gap < 0
                          ? 'tshirt-gap tshirt-gap--short'
                          : row.gap > 0
                          ? 'tshirt-gap tshirt-gap--surplus'
                          : 'tshirt-gap tshirt-gap--zero'
                      }
                    >
                      {row.gap > 0 ? `+${row.gap}` : row.gap}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="tshirt-table-footer-actions">
              <button
                type="button"
                className="tshirt-btn tshirt-btn--ghost"
                onClick={handleSaveInventory}
                disabled={inventorySaving || !inventoryDirty}
              >
                {inventorySaving ? 'Saving…' : 'Save inventory'}
              </button>
              {inventorySaveStatus && (
                <span className="tshirt-save-status" role="status" aria-live="polite">
                  {inventorySaveStatus}
                </span>
              )}
            </div>
          </div>
          <div className="tshirt-allocation-actions">
            <button
              type="button"
              className="tshirt-btn btn-fm-primary"
              onClick={() => setShowAllocateModal(true)}
              disabled={allocating || !people.length}
            >
              {allocating ? 'Working…' : 'Allocate Now'}
            </button>
            <button
              type="button"
              className="tshirt-btn tshirt-btn--danger"
              onClick={openResetModal}
              disabled={!people.length}
            >
              Reset allocations
            </button>
          </div>
        </div>

        <div className="tshirt-section tshirt-section--roster">
          <div className="tshirt-section-head">
            <h3 className="tshirt-section-title">Roster &amp; labels</h3>
            <div className="tshirt-roster-header-actions">
              <button
                type="button"
                className="tshirt-btn tshirt-btn--ghost"
                onClick={() => openTshirtTableExport('bagList', 'print')}
                disabled={dataLoading || !people.length}
              >
                Print bag list
              </button>
              <AdminCopyMenu
                ariaLabel="Copy bag list"
                disabled={dataLoading || !people.length}
                items={[
                  {
                    label: 'Copy bag list…',
                    onSelect: () => openTshirtTableExport('bagList', 'copy'),
                    showCopiedFeedback: false,
                  },
                ]}
              />
              <button
                type="button"
                className="tshirt-btn tshirt-btn--ghost"
                onClick={() => openTshirtTableExport('allocations', 'print')}
                disabled={dataLoading || !people.length}
              >
                Print full roster
              </button>
              <AdminCopyMenu
                ariaLabel="Copy full roster table"
                disabled={dataLoading || !people.length}
                items={[
                  {
                    label: 'Copy full roster…',
                    onSelect: () => openTshirtTableExport('allocations', 'copy'),
                    showCopiedFeedback: false,
                  },
                ]}
              />
              <button
                type="button"
                className="tshirt-btn tshirt-btn--ghost"
                onClick={handleSaveAllocations}
                disabled={savingAllocations}
                title="Re-save every roster row to Firestore (dropdown changes already save automatically)"
              >
                {savingAllocations ? 'Saving…' : 'Save all'}
              </button>
              {allocationsSaveStatus && (
                <span className="tshirt-save-status" role="status" aria-live="polite">
                  {allocationsSaveStatus}
                </span>
              )}
            </div>
          </div>
          <p className="tshirt-section-lede">
            <strong>Allocated:</strong> <strong>Not set</strong> = no shirt assignment yet;{' '}
            <strong>None (no shirt)</strong> = deliberate choice (not counted in &quot;not allocated&quot;
            warnings). Changing the dropdown saves immediately. Use <strong>Save all</strong> only if you
            want to re-sync everything. Label source: type, name, requested vs allocated, and swap notes.
            Rows with a size swap are highlighted. Volunteers: add below, bulk import, or (for events
            using Fury Registration API) sync from Admin Dashboard Import all when registrations include
            volunteer roles.
          </p>
          <div className="tshirt-volunteer-toolbar">
            <button
              type="button"
              className={
                showBulkVolunteerImport
                  ? 'tshirt-btn tshirt-btn--ghost tshirt-btn--sm'
                  : 'tshirt-btn btn-fm-primary tshirt-btn--sm'
              }
              onClick={() => setShowBulkVolunteerImport((prev) => !prev)}
            >
              {showBulkVolunteerImport ? 'Hide bulk import' : 'Bulk import volunteers'}
            </button>
          </div>
          {showBulkVolunteerImport && (
            <div className="tshirt-volunteer-import">
              <label>
                Paste markdown or pipe table: <code>Name | Email | Requested Size</code>
                <textarea
                  rows={5}
                  value={bulkVolunteerInput}
                  onChange={(e) => setBulkVolunteerInput(e.target.value)}
                  placeholder="| Name | Email | Requested Size |&#10;| --- | --- | --- |&#10;| Jane Doe | jane@example.com | MM |"
                />
              </label>
              <div className="tshirt-volunteer-import-actions">
                <button
                  type="button"
                  className="tshirt-btn btn-fm-primary tshirt-btn--sm"
                  onClick={handleBulkImportVolunteers}
                  disabled={bulkVolunteerSaving || !bulkVolunteerInput.trim() || !selectedEventId}
                >
                  {bulkVolunteerSaving ? 'Importing…' : 'Import'}
                </button>
              </div>
              {bulkVolunteerStatus && (
                <div className="tshirt-volunteer-import-status">{bulkVolunteerStatus}</div>
              )}
            </div>
          )}
          <form className="tshirt-volunteer-form" onSubmit={handleAddVolunteer}>
            <div className="tshirt-volunteer-fields">
              <span className="tshirt-volunteer-form-label">Add volunteer</span>
              <label>
                Name
                <input
                  type="text"
                  value={volunteerForm.name}
                  onChange={(e) => handleVolunteerFieldChange('name', e.target.value)}
                />
              </label>
              <label>
                Email
                <input
                  type="email"
                  value={volunteerForm.email}
                  onChange={(e) => handleVolunteerFieldChange('email', e.target.value)}
                />
              </label>
              <label>
                Requested size
                <input
                  type="text"
                  value={volunteerForm.tShirtRequested}
                  onChange={(e) =>
                    handleVolunteerFieldChange('tShirtRequested', e.target.value)
                  }
                  placeholder="e.g. MM"
                />
              </label>
              <label className="tshirt-volunteer-notes">
                Notes
                <input
                  type="text"
                  value={volunteerForm.notes}
                  onChange={(e) => handleVolunteerFieldChange('notes', e.target.value)}
                />
              </label>
              <button type="submit" className="tshirt-btn btn-fm-primary" disabled={volunteerSaving}>
                {volunteerSaving ? 'Adding…' : 'Add volunteer'}
              </button>
            </div>
          </form>
          <div className="tshirt-roster-filters">
            <label className="tshirt-filter-label">
              Filter roster
              <select
                value={allocatedSizeFilter}
                onChange={(e) => setAllocatedSizeFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value={ROSTER_FILTER_UNASSIGNED}>Not set only</option>
                {SIZE_ORDER.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <div className="tshirt-export-cluster">
              <button
                type="button"
                className="tshirt-btn tshirt-btn--ghost tshirt-btn--sm"
                onClick={() => openTshirtTableExport('volunteerRoster', 'print')}
                disabled={dataLoading || volunteers.filter((v) => !v.cancelled).length === 0}
              >
                Print volunteer roster
              </button>
              <AdminCopyMenu
                ariaLabel="Copy volunteer roster"
                disabled={dataLoading || volunteers.filter((v) => !v.cancelled).length === 0}
                items={[
                  {
                    label: 'Copy volunteer roster…',
                    onSelect: () => openTshirtTableExport('volunteerRoster', 'copy'),
                    showCopiedFeedback: false,
                  },
                ]}
              />
            </div>
          </div>
          {dataLoading ? (
            <p>Loading roster…</p>
          ) : (() => {
            const rosterPeople = rosterSortedPeople.filter((p) => {
              if (
                p.flyerType !== 'freshling' &&
                p.flyerType !== 'mentor' &&
                p.flyerType !== 'volunteer'
              ) {
                return false;
              }
              if (!allocatedSizeFilter) return true;
              if (allocatedSizeFilter === ROSTER_FILTER_UNASSIGNED) {
                return isPersonTshirtTrulyUnallocated(p, pendingAllocatedByKey);
              }
              const eff = getEffectiveAllocated(p, pendingAllocatedByKey);
              return eff === allocatedSizeFilter;
            });
            if (!rosterPeople.length) {
              return <p>No active freshlings, mentors, or volunteers for this event.</p>;
            }
            return (
              <>
                {shortfallBarItems.length > 0 && (
                  <div
                    className="tshirt-shortfall-sticky-bar"
                    role="status"
                    aria-live="polite"
                  >
                    <span className="tshirt-shortfall-sticky-label">Shortfall</span>
                    <div className="tshirt-shortfall-sticky-chips">
                      {shortfallBarItems.map((r, i) => (
                        <React.Fragment key={r.size}>
                          {i > 0 ? <span className="tshirt-shortfall-sep">·</span> : null}
                          <span className="tshirt-shortfall-chip">
                            {r.size}
                            {' \u2212'}
                            {Math.abs(r.gap)}
                          </span>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                )}
                <div className="tshirt-roster-table-wrap">
                <table className="tshirt-table tshirt-table--roster">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Notes</th>
                      <th>Requested</th>
                      <th>Allocated</th>
                      <th>Swap note</th>
                      <th>Bag</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rosterPeople.map((p) => {
                      const key = p.key;
                      const requested = p.requestedSize || '';
                      const allocatedRaw = getEffectiveAllocated(p, pendingAllocatedByKey);
                      const allocatedSelectValue = allocatedSelectValueFromEffective(allocatedRaw);
                      const unknownAllocated =
                        allocatedSelectValue !== '' &&
                        allocatedSelectValue !== NO_SHIRT_VALUE &&
                        !SIZE_ORDER.includes(allocatedSelectValue);
                      const allocatedLabel =
                        allocatedSelectValue === ''
                          ? 'Not set'
                          : allocatedSelectValue === NO_SHIRT_VALUE
                          ? 'None (no shirt)'
                          : allocatedSelectValue;
                      const allocatedForSwap =
                        allocatedRaw === NO_SHIRT_VALUE ? '' : allocatedRaw;
                      const swapNote = buildSwapNoteText(p, allocatedRaw, requested);
                      const isSwap = Boolean(
                        allocatedForSwap &&
                          requested &&
                          normalizeSize(allocatedForSwap) !== normalizeSize(requested)
                      );
                      const noRequest = p.flyerType === 'freshling' && !requested;
                      const typeLabel = getTypeLabelForPerson(p);
                      const vol =
                        p.flyerType === 'volunteer'
                          ? flyers.find((f) => f.id === p.id && f.type === 'volunteer')
                          : null;
                      const rowClass = [
                        isSwap ? 'tshirt-row-swap' : '',
                        noRequest ? 'tshirt-row-no-size' : '',
                        !allocatedRaw ? 'tshirt-row-allocation-unset' : '',
                        allocatedRaw === NO_SHIRT_VALUE ? 'tshirt-row-allocation-none' : '',
                      ]
                        .filter(Boolean)
                        .join(' ');
                      return (
                        <tr key={key} className={rowClass || undefined}>
                          <td>{typeLabel}</td>
                          <td>
                            {p.flyerType === 'volunteer' && vol ? (
                              <input
                                type="text"
                                className="tshirt-roster-input"
                                value={vol.name || ''}
                                onChange={(e) =>
                                  setFlyers((prev) =>
                                    prev.map((x) =>
                                      x.id === vol.id ? { ...x, name: e.target.value } : x
                                    )
                                  )
                                }
                                onBlur={() => handleUpdateVolunteer(vol)}
                              />
                            ) : (
                              p.displayName
                            )}
                          </td>
                          <td>
                            {p.flyerType === 'volunteer' && vol ? (
                              <input
                                type="email"
                                className="tshirt-roster-input"
                                value={vol.email || ''}
                                onChange={(e) =>
                                  setFlyers((prev) =>
                                    prev.map((x) =>
                                      x.id === vol.id ? { ...x, email: e.target.value } : x
                                    )
                                  )
                                }
                                onBlur={() => handleUpdateVolunteer(vol)}
                              />
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>
                            {p.flyerType === 'volunteer' && vol ? (
                              <input
                                type="text"
                                className="tshirt-roster-input"
                                value={vol.notes || ''}
                                onChange={(e) =>
                                  setFlyers((prev) =>
                                    prev.map((x) =>
                                      x.id === vol.id ? { ...x, notes: e.target.value } : x
                                    )
                                  )
                                }
                                onBlur={() => handleUpdateVolunteer(vol)}
                              />
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>{requested || '—'}</td>
                          <td>
                            <select
                              className="tshirt-roster-size-select"
                              value={allocatedSelectValue}
                              onChange={(e) =>
                                handleManualAllocatedChange(key, e.currentTarget.value)
                              }
                              size={1}
                              aria-label={`Allocated size (${allocatedLabel}) for ${p.displayName || 'person'}`}
                            >
                              <option value="">Not set</option>
                              <option value={NO_SHIRT_VALUE}>None (no shirt)</option>
                              {unknownAllocated ? (
                                <option value={allocatedSelectValue}>
                                  {allocatedSelectValue} (unlisted)
                                </option>
                              ) : null}
                              {SIZE_ORDER.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            {swapNote ? (
                              <span className="tshirt-swap-badge">{swapNote}</span>
                            ) : (
                              ''
                            )}
                          </td>
                          <td>
                            {p.flyerType === 'volunteer' ? (
                              <input
                                type="checkbox"
                                checked={!!p.goodyBag}
                                onChange={(e) =>
                                  handleVolunteerGoodyBagChange(p.id, e.target.checked)
                                }
                                title="Goody bag"
                                aria-label="Goody bag"
                              />
                            ) : p.flyerType === 'freshling' || p.flyerType === 'mentor' ? (
                              <input
                                type="checkbox"
                                checked
                                disabled
                                title="Goody bag included by default"
                                aria-label="Goody bag included by default"
                              />
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>
                            {p.flyerType === 'volunteer' && vol ? (
                              <button
                                type="button"
                                className="tshirt-volunteer-delete"
                                onClick={() => handleDeleteVolunteer(vol.id)}
                              >
                                Delete
                              </button>
                            ) : (
                              ''
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </>
            );
          })()}
        </div>
      </div>

      {showAllocateModal && (
        <div
          className="fm-modal-overlay"
          role="presentation"
          onClick={() => {
            if (!allocating) setShowAllocateModal(false);
          }}
        >
          <div
            className="fm-modal fm-modal--sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tshirt-allocate-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="tshirt-allocate-title">
                Allocate now?
              </h2>
              <button
                type="button"
                className="fm-modal-close"
                onClick={() => !allocating && setShowAllocateModal(false)}
                aria-label="Close"
                disabled={allocating}
              >
                ✕
              </button>
            </div>
            <form
              noValidate
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                if (!form.checkValidity()) {
                  setAllocateFormInvalidNames(collectInvalidNamedControlNames(form));
                  const inv = form.querySelector(':invalid');
                  if (inv && typeof inv.focus === 'function') inv.focus();
                  return;
                }
                setAllocateFormInvalidNames(new Set());
                if (allocating) return;
                setAllocating(true);
                try {
                  const ok = await allocateNow();
                  if (ok) setShowAllocateModal(false);
                } finally {
                  setAllocating(false);
                }
              }}
            >
              <div className="fm-modal-body">
                <p className="tshirt-modal-confirm-text">
                  This fills only unassigned roster rows with each person&apos;s requested size and saves
                  to Firestore.
                </p>
                <input
                  type="text"
                  name="allocateHasRoster"
                  readOnly
                  required
                  value={people.length ? 'ok' : ''}
                  tabIndex={-1}
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    padding: 0,
                    margin: -1,
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                  }}
                />
                <ModalFieldRequiredError
                  invalidNames={allocateFormInvalidNames}
                  fieldName="allocateHasRoster"
                />
              </div>
              <div className="fm-modal-footer">
                <button
                  type="button"
                  className="fm-modal-btn fm-modal-btn-cancel"
                  disabled={allocating}
                  onClick={() => setShowAllocateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="fm-modal-btn fm-modal-btn-primary" disabled={allocating}>
                  {allocating ? 'Working…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resetModalOpen && (
        <div
          className="fm-modal-overlay"
          role="presentation"
          onClick={closeResetModal}
          onKeyDown={(e) => e.key === 'Escape' && closeResetModal()}
        >
          <div
            className="fm-modal fm-modal--sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tshirt-reset-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="tshirt-reset-title">
                Reset all allocations?
              </h2>
              <button type="button" className="fm-modal-close" onClick={closeResetModal} aria-label="Close">
                ✕
              </button>
            </div>
            <div className="fm-modal-body">
              <p className="tshirt-modal-confirm-text">
                Reset all allocations? This will clear every allocated size for everyone on the roster
                and cannot be undone.
              </p>
            </div>
            <div className="fm-modal-footer">
              <button type="button" className="fm-modal-btn fm-modal-btn-cancel" onClick={closeResetModal}>
                Cancel
              </button>
              <button type="button" className="fm-modal-btn fm-modal-btn-destructive" onClick={applyResetAllPending}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {tshirtTableExport && (
        <AdminColumnExportModal
          mode={tshirtTableExport.mode}
          columns={getColumnsForTshirtExport(tshirtTableExport.ctx)}
          selectedIds={tshirtTableExportCols}
          onChangeSelected={setTshirtTableExportCols}
          onCancel={() => setTshirtTableExport(null)}
          onConfirm={runTshirtTableExport}
          onResetDefaults={resetTshirtTableExportCols}
        />
      )}
    </div>
  );
}

export default TShirtManagement;

